import { VoyagePlan } from "../../../src/models/vessel-planning-model";
import { UIRouteWithMetadata, VoyageRoute, VoyageRouteTotal, VoyageRouteWeather, WeatherRoutingInfo } from "../../../src/models/voyage-routing-model";
import { getPilotPointIndeces, getRouteDistance, getRoutePropertyAverages } from "../../../src/utils/route-utils";
import { CalculatedValues, StarRoute1, UIRouteWithCalculatedValues } from "./types";
import { css } from 'lit';


export enum WRStatus  {
    Read = 'read',
    UnRead = 'unread',
    Accept = 'accept',
    Reject = 'reject'
}


export enum WniRequestLabels {
    NVI = 'Initial Plan',
    RTA = 'Update RTA',
    DEST = 'Update Destination',
    ROUTE = 'StarConnect Route Change',
    REGULAR = 'Regular Update'
}

export function getBorderColor(index : number) {
    switch (index) {
        case 0:
            return '#1D7DEA';
        case 1:
            return '#1DC6BC';
        case 2:
            return '#D61E92';
        case 3:
            return '#611ECF';
        case 4:
            return '#F77F00';
    }
}
export function getBackgroundColor(index : number) {
    switch (index) {
        case 0:
            return '#1D7DEA0D';
        case 1:
            return '#1DC6BC0D';
        case 2:
            return '#D61E920D';
        case 3:
            return '#611ECF0D';
        case 4:
            return '#F77F000D';
        }
    }
export function  getDefaultCalculatedValues(): CalculatedValues {
    return {
        fuel: "-",
        distance: "-",
        distancePilotToPilot: "-",
        avgSpeed: "-",
        avgSpeedPilotToPilot: "-",
        maxwind: "-",
        maxwave: "-",
        maxswell: "-",
        name: "-"
    };
}

export function formatCountdown(seconds: number): string {
    const minutes = Math.max(1, Math.ceil(Math.max(0, seconds) / 60));
    return `${minutes} min`;
}

export function showDeleteIcon(selectedRouteId: string, routeId: string, name: string, routeCreatedBy?: string) {
    if (name === StarRoute1 || (routeCreatedBy && routeCreatedBy === 'WR') || selectedRouteId === routeId) {
      return false;
    }
    return true;
  }

export function AddWeatherRoutingInfoObject(route: VoyageRoute) : VoyageRoute {
    route.weatherRoutingInfo = {
        required: false,
        required_reason: '',
        status: undefined
    }
    return route;
}

/** Preserves executing-plan WR flags when sending status updates to the Weather Routing API. */
export function mergeWrInfoForStatusUpdate(
    plan: VoyagePlan,
    incoming: WeatherRoutingInfo = { required: false },
): WeatherRoutingInfo {
    const selectedWrInfo = plan.routes[plan.selectedRouteIndex]?.weatherRoutingInfo;
    const wasWrRequired = selectedWrInfo?.required === true || plan.wr_enabled === true;

    return {
        ...incoming,
        required: wasWrRequired || incoming.required === true,
        required_reason: selectedWrInfo?.required_reason || incoming.required_reason || '',
    };
}

export function checkifValidRoute(route:VoyageRoute): boolean {

    // valid route should have 2 pilot points
    const pilotIndices = getPilotPointIndeces(route);
    if (pilotIndices.length < 2) {
        return false;
    }

    return true;
}

    export function calculateRouteValues(route: VoyageRoute, index: number): CalculatedValues {
        const KmToNM = 1 / 1.852;
        const avgSpeed = calculateAvgSpeed(KmToNM, route.route_info?.speed_over_ground);
        const pilotIndices = getPilotPointIndeces(route);
        const [departurePilot, arrivalPilot] = [pilotIndices[0], pilotIndices[pilotIndices.length - 1]];
        const averages = pilotIndices.length > 1 ? getRoutePropertyAverages(route, departurePilot, arrivalPilot) : null;
        const avgSpeedPilotToPilot = averages && Number.isFinite(averages.speed) ? `${(averages.speed * 3.6 * KmToNM).toFixed(1)} kt` : "-";
        const distancePilotToPilot = pilotIndices.length > 1 ? `${(getRouteDistance(route, departurePilot, arrivalPilot) * KmToNM).toFixed(1)} nm` : "-";
        const fuel = calculateTotalFuel(route.route_info?.totals);
        const [maxwind, maxwave, maxswell] = getWeatherValues(route.route_info?.weather);
        const distance = route.route_info?.totals?.non_eca?.distance ? `${(route.route_info.totals.non_eca.distance * KmToNM).toFixed(1)} nm` : '-';
        const isBpr = route?.routeCreatedBy === 'system';
        const name = isBpr ? `${route.name}` : route.name ? `${route.name}` : `Own route ${index}`;

    
        let calculatedValues = {
            fuel,
            distance: distance,
            distancePilotToPilot,
            avgSpeed,
            avgSpeedPilotToPilot,
            maxwind,
            maxwave,
            maxswell,
            name: name
        };
    
        return calculatedValues;
    }
    
    function calculateAvgSpeed(conversionFactor: number, speedOverGround?:number): string {
        if(!speedOverGround) return '-';
        return speedOverGround ? `${(speedOverGround * 3.6 * conversionFactor).toFixed(1)} kt` : '-';
    }
    
    export function calculateTotalFuel(totals?: VoyageRouteTotal): string {
        if (!totals) return '-';
        if (totals && totals.non_eca && totals.non_eca.fuel) {
            const allFuel = totals.non_eca.fuel + (totals.eca ? totals.eca.fuel : 0);
            return `${Math.round(allFuel)} MT`;
        }
        return '-';
    }
    export function getWeatherValues(weather?: VoyageRouteWeather): [string, string, string] {
        if (!weather) return ['-', '-', '-'];
        let maxwind = '-', maxwave = '-', maxswell = '-';
        if (weather) {
            const wind10 = weather?.summary?.Wind10M;
            if (wind10) {
                maxwind = `${wind10.max.value.toFixed(1)} m/s`;
            }
            const wave = weather?.summary?.Wave;
            if (wave) {
                maxwave = `${wave.max.value.toFixed(1)} m`;
            }
            const swell = weather?.summary?.Swell;
            if (swell) {
                maxswell = `${swell.max.value.toFixed(1)} m`;
            }
        }
        return [maxwind, maxwave, maxswell];
    }

    export const getSegregatedRouteIndices = (routeId : string,routes: UIRouteWithCalculatedValues[]) => {
        const combinedIndexes: { [key: string]: number } = {};

        const bprRoutes = routes.filter(route => route.route?.routeCreatedBy === 'system');
        const userRoutes = routes.filter(route => route.route?.routeCreatedBy === 'bridge');
        
        bprRoutes.forEach((route, index) => {
            if(route.route?.routeId){
                combinedIndexes[route.route.routeId] = index;
            }
        });

        userRoutes.forEach((route, index) => {
            if(route.route?.routeId){
                combinedIndexes[route.route.routeId] = index;
            }
        });

        const idx = combinedIndexes[routeId];
        return idx !== undefined ? idx + 1 : 0;
    }

    export const starRouteFetchDisabled = (etd: number) => {
        const currentDate = new Date().getTime();
        if (etd > currentDate)
        {
            return false;
        }     
        return true;
    }

    // pass selected route id and plan to check if selected route is more fuel efficient than star route 1
    // selected route id should always be a own route
    export const checkIfOwnRouteIsMoreFuelEfficientThanStarRouteOne = (plan: VoyagePlan, selectedRouteId: string) => {

        const starRoute = plan.routes.find(route => route.name === StarRoute1);
        const ownRoute = plan.routes.find(route => route.routeId === selectedRouteId);

        if(!starRoute) return true;
        if(!ownRoute) return false;

        const { non_eca: ownNonECA, eca: ownECA } = ownRoute.route_info?.totals || {};
        const { non_eca: starNonECA, eca: starECA } = starRoute.route_info?.totals || {};
    
        const totalOwnFuel = (ownNonECA?.fuel ?? 0) + (ownECA?.fuel ?? 0);
        const totalStarFuel = (starNonECA?.fuel ?? 0) + (starECA?.fuel ?? 0);
    
        return totalOwnFuel < totalStarFuel;
    };

    export const voyageSuggestedStyles = css`
                .container {
                    font-family: 'MaerskText';
                    background-color: #fff;
                    padding: 8px 0;
                    text-align: center;
                    position: -webkit-sticky;
                    position: sticky;
                    top: 0;
                    z-index: 500;
                    border-bottom: 1px solid transparent;
                    box-shadow: 0px 4px 6px -1px rgba(0, 0, 0, 0.1);
                    margin-bottom: 16px;
                }
                
                .contanier-route-modal{
                    display: flex;
                    position: relative;
                    grid-template-columns: 100%;
                    font-family: MaerskText;
                    align-items: center;
                    justify-content: center;
                    gap:15px;
                    width: max-content;
                }
                .scheduleinfo {
                    border-radius: 2px;
                    margin: 1px;
                    display: grid;
                    grid-template-columns: 50% 50%;
                    grid-template-rows: repeat(2, max-content);
                    grid-auto-flow: column;
                    margin-bottom: 8px;
                    outline: 1px solid var(--neutrals-grey-07, grey);
                    background: var(--neutrals-grey-09, white);
                }
                .route-card {
                    outline: none;
                    background: none;
                }
                .route-crad .scheduleborder {
                    border: none;
                    background: none;
                }
                .scheduleborder {
                    grid-row: 1 / -1;
                }
                .schedulecell[legendHeader] {
                    font-size: 10px;
                    font-weight: 700;
                    line-height: 16px;
                    margin-top: 5px;
                    margin-bottom: 0;
                    align-items: flex-end;
                    text-align: left;
                    padding-left: 8px;
                }
                .schedulecell[legend] {
                    align-items: flex-end;
                    text-align: left;
                    padding-left: 8px;
                    font-size: 12px;
                    margin-bottom: 5px;
                    color: var(--neutrals-grey-04, grey);
                }
                .gridcontainer {
                    margin: 1px;
                    display: grid;
                    grid-template-columns: 86px 86px 86px 86px;
                    grid-template-rows: repeat(13, max-content);
                    grid-auto-flow: column;
                    grid-gap: 1px;
                }
                .cell {
                    display: flex;
                    flex-direction: column;
                    align-content: center;
                    align-items: center;
                    justify-content: space-around;
                    color: var(--neutrals-grey-02, black);
                    padding-right: 5px;
                    padding-left: 5px;
                    font-size: 14px;
                    text-align: center;
                    margin-top: 5px;
                    margin-bottom: 5px;
                }
                .row1 { grid-row: 2 / 2; }
                .row2 { grid-row: 3 / 3; }
                .row3 { grid-row: 4 / 4; }
                .row4 { grid-row: 5 / 5; }
                .row5 { grid-row: 6 / 6; }
                .row6 { grid-row: 7 / 7; }
                .row7 { grid-row: 8 / 8; }
                .row8 { grid-row: 9 / 9; }
                .row9 { grid-row: 10 / 10; }
                .row10 { grid-row: 11 / 11; }
                .row11 { grid-row: 12 / 12; }
                .row12 { grid-row: 13 / 13; }
                .row13 { grid-row: 14 / 14; }
                .row14 { grid-row: 15 / 15; }
                .row15 { grid-row: 16 / 16; }
                .cell[legendHeader] {
                    grid-column: 1;
                    color: var(--neutrals-grey-01,grey);
                    font-size: 12px;
                    line-height: 16px;
                    margin-top: 20px;
                    margin-bottom: 0;
                    align-items: flex-start;
                    font-weight: 700;
                }
                .cell[columnHeader] {
                    grid-row: 1 / 1;
                    height: 110px;
                    font-size: 12px;
                    line-height: 16px;
                    text-align: center;
                    margin: 0;
                    outline: 1px solid var(--neutrals-grey-07, grey);
                }
                .cell2 {
                    font-size: 12px;
                }
                .cell[route="1"][selected] {
                    outline: 1px solid var(--route1-color, green);
                }
                .cell[route="2"][selected] {
                    outline: 1px solid var(--route2-color, orange);
                }
                .cell[route="3"][selected] {
                    outline: 1px solid var(--route3-color, purple);
                }
                .cell[route="4"][selected] {
                    outline: 1px solid var(--route4-color, purple);
                }
                .cell[route="5"][selected] {
                    outline: 1px solid var(--route5-color, purple);
                }
                *[column="1"] { grid-column: 1 / 1; }
                *[column="2"] { grid-column: 2 / 2; }
                *[column="3"] { grid-column: 3 / 3; }
                *[column="4"] { grid-column: 4 / 4; }
                *[row="1"] { grid-row: 1 / 1; }
                *[row="2"] { grid-row: 2 / 2; }
                .routeIcon[route="1"] {
                    stroke: var(--route1-color, green);
                }
                .routeIcon[route="2"] {
                    stroke: var(--route2-color, orange);
                }
                .routeIcon[route="3"] {
                    stroke: var(--route3-color, purple);
                }
                .routeIcon[route="4"] {
                    stroke: var(--route4-color, purple);
                }
                .routeIcon[route="5"] {
                    stroke: var(--route5-color, purple);
                }
                .optionalText {
                    color: var(--neutrals-grey-05, grey);
                }
                .legend {
                    grid-column: 1;
                    grid-row: 2 / -1;
                    outline: 1px solid var(--neutrals-grey-07, grey);
                    background-color: var(--neutrals-grey-09, white);
                }
                .cell[legend] {
                    grid-column: 1;
                    align-items: flex-start;
                    text-align: right;
                    font-size: 12px;
                }
                .route1 {
                    grid-row: 1 / -1;
                    outline: 1px solid var(--neutrals-grey-07, grey);
                    pointer-events: none;
                }
                .route1[selected] {
                    background-color: var(--route1-transparent-bg-color, white);
                    outline: 1px solid var(--route1-color, green);
                    z-index: 1;
                }
                .route2 {
                    grid-row: 1 / -1;
                    outline: 1px solid var(--neutrals-grey-07, grey);
                    pointer-events: none;
                }
                .route2[selected] {
                    background-color: var(--route2-transparent-bg-color, white);
                    outline: 1px solid var(--route2-color, orange);
                    z-index: 1;
                }
                .route3 {
                    grid-row: 1 / -1;
                    outline: 1px solid var(--neutrals-grey-07, grey);
                    pointer-events: none;
                }
                .route3[selected] {
                    background-color: var(--route3-transparent-bg-color, white);
                    outline: 1px solid var(--route3-color, purple);
                    z-index: 1;
                }
                .route4 {
                    grid-row: 1 / -1;
                    outline: 1px solid var(--neutrals-grey-07, grey);
                    pointer-events: none;
                }
                .route4[selected] {
                    background-color: var(--route4-transparent-bg-color, white);
                    outline: 1px solid var(--route4-color, orange);
                    z-index: 1;
                }
                .route5 {
                    grid-row: 1 / -1;
                    outline: 1px solid var(--neutrals-grey-07, grey);
                    pointer-events: none;
                }
                .route5[selected] {
                    background-color: var(--route4-transparent-bg-color, white);
                    outline: 1px solid var(--route5-color, orange);
                    z-index: 1;
                }
                .routeInfo {
                    flex: 1;
                    margin: 5px;
                    box-sizing: border-box;    
                }
                .routeName {
                    display: flex;
                    position: relative;
                    flex-direction: column;
                    top: 30px;
                }
                .routeTitle {
                    width: 60px;
                    padding: 5px;
                    overflow: hidden;
                    white-space: nowrap;
                    text-overflow: ellipsis;
                }
                .questionIcon {
                    margin-top: 10px;
                    margin-bottom: -27px;
                }
                input[type="file"] {
                    display: none;
                }
                .fill {
                    grid-row: 2 / -1;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    flex-direction: column;
                    color: var(--neutrals-grey-02, black);
                    text-align: center;
                    font-family: 'MaerskText';
                    font-size: 10px;
                    line-height: 14px;
                    text-align: center;
                }
                .createNew {
                    color: var(--primary-blue-05, blue);
                    font-family: 'MaerskText';
                    font-size: 12px;
                    background: none;
                    outline: none;
                    border: none;
                }
                .createNewDissabled {
                    color: var(--neutrals-grey-02, blue);
                    font-family: 'MaerskText';
                    font-size: 12px;
                    background: none;
                    outline: none;
                    border: none;
                }
                .reupload {
                    color: var(--primary-blue-05, blue);
                    font-family: 'MaerskText';
                    font-size: 12px;
                    background: none;
                    outline: none;
                    border: none;
                    cursor: pointer;
                }
                .or {
                    margin-top: 5px;
                    margin-bottom: 5px;
                }
                .upload {
                    display: flex;  
                    flex: 1;
                    flex-direction: column;
                    align-content: center;
                    align-items: center;
                    justify-content: space-around;
                    border: 1px solid var(--neutrals-grey-06, grey);
                    box-sizing: border-box;
                    border-radius: 3px;    
                    background-color: white;
                    height: 32px;
                    color: var(--neutrals-grey-02, black);
                    cursor: pointer;
                    font-family: 'MaerskText';
                    font-size: 14px;
                    font-weight: 400;
                }
                .selectButton {
                    position: relative;
                    bottom: -35px;
                    padding: 1px 7px 1px 7px;
                    width: 65px;
                    height: 20px;
                    border-radius: 10px;
                    display: flex;
                    flex-direction: row;
                    justify-content: space-between;
                    align-items: center;
                    font-family: 'MaerskText';
                    font-size: 10px;
                    line-height: 14px;
                    text-align: right;
                    outline: none;
                    fill: var(--neutrals-grey-10, white);
                    color: var(--neutrals-grey-10, white);
                }
                .selectButton[route="1"] {
                    background-color: var(--route1-color, green);
                }
                .selectButton[route="2"] {
                    background-color: var(--route2-color, orange);
                }
                .selectButton[route="3"] {
                    background-color: var(--route3-color, purple);
                }
                .selectButton[route="4"] {
                    background-color: var(--route4-color, purple);
                }
                .selectButton[route="5"] {
                    background-color: var(--route5-color, purple);
                }
                .selectButton[disabled] {
                    padding: 3px 9px 3px 9px;
                    background-color: var(--neutrals-grey-07, grey);
                    color: var(--neutrals-grey-02, black);
                    fill: var(--neutrals-grey-02, black);
                    border: none;
                }
                .selectButton > svg {
                    display: block;
                    height: 11px;
                }
                .standardButton {
                    position: relative;
                    background: none;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    box-sizing: border-box;
                    height: 32px;
                    color: var(--neutrals-grey-02, black);
                    fill: currentColor;
                    font-family: 'MaerskText';
                    font-size: 14px;
                    outline: none;
                    border-radius: 2.5px;
                    border: 1px solid var(--neutrals-grey-06, grey);
                }
                .standardButton[disabled] {
                    filter: opacity(0.5);
                }
                .planStatus {
                    position: absolute;
                    border-radius: 10px;
                    border: 1px solid var(--warning-yellow-7, yellow);
                    color: var(--warning-yellow-7, yellow);
                    font-size: 10px;
                    line-height: 14px;
                    padding-right: 5px;
                    padding-left: 5px;
                    padding-top: 1px;
                    padding-bottom: 1px;
                    right: 15px;
                    top: 50%;
                    transform: translateY(-50%);
                }
                /* background with opacity */
                .planStatus::before {
                    content: "";
                    position: absolute;
                    border-radius: inherit;
                    top: 0;
                    bottom: 0;
                    right: 0;
                    left: 0;
                    background-color: currentColor;
                    opacity: 0.07;
                }
                .planStatus[approved] {
                    border: 1px solid var(--approved-plan-color, green);
                    color: var(--approved-plan-color, green);
                }
                .buttonIcon {
                    margin-right: 5px;
                    fill: var(--neutrals-grey-02, black);
                }
                .buttonIcon > svg {
                    display: block;
                    height: 14px;
                }
                .routeStatus {
                    grid-row: 2 / -1;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    flex-direction: column;
                    font-size: 12px;
                    color: var(--neutrals-grey-02, black);
                    text-align: center;
                }
                .fill .routeStatus[error] {
                    grid-row: none;
                    color: var(--voyage-error-red, red);
                    font-size: 12px;
                    text-align: center;
                    padding: 0px 0px 10px;
                }
                .buttonRow {
                    margin-top: 10px;
                    margin-bottom: 10px;
                    display: flex;
                    justify-content: space-between;
                    width: 100%;
                }
                #routeEditor {
                    width: 156px;
                }
                #passagePlan {
                    width: 100%;
                    height: 30px;
                }
                
                voyage-spinner {
                    height: 28px;
                    width: 28px;
                    --voyage-spinner-bg-color: var(--primary-blue-0, grey);
                    --voyage-spinner-stroke-width: 3px;
                }
                voyage-route-export {
                    width: 100%;
                    margin-top: 10px;
                }
                .delete-button {
                    color: var(--waypoint-editor-delete-button-color, red);
                    padding: 0;
                    border: none;
                    background: none;
                }
                .delete-button > svg {
                    fill: currentColor;
                    height: 12px;
                }
                .zone-apply {
                    color: var(--primary-blue-05, blue);
                    font-family: 'MaerskText';
                    font-size: 12px;
                    background: none;
                    outline: none;
                    border: none;
                    cursor: pointer;
                }
                .zones-applied {
                    color: var(--primary-blue-05, blue);
                    font-family: 'MaerskText';
                    font-size: 12px;
                    background: none;
                    outline: none;
                    border: none;
                    cursor: auto;
                }
                section {
                    display: flex;
                    justify-content: flex-start;
                    align-items: center;
                }
                section .spinner-wrapper {
                    position: relative;
                    height: 50px;
                    width: 20%;
                    background-color: var(--neutrals-grey-10);
                }
                section .spinner-wrapper voyage-spinner {
                    width: 30px;
                    height: 30px;
                    left: calc(50% - 25px);
                    top: calc(50% - 25px);
                    position: absolute;
                }
                
                .no-autoroute {
                    display: block;
                    padding: 12px;
                    background-color: #B5E0F5;
                    border-radius: 5px;
                    margin-bottom: 8px;
                }
                
                .no-autoroute p {
                    font-family: MaerskText;
                    font-size: 14px;
                    font-weight: 400;
                    line-height: 20px;
                    letter-spacing: 0em;
                    text-align: left;
                    margin: 0;
                    padding: 0;
                }
                .bp-r-2 {
                    font-family: Maersk Text;
                    font-size: 14px;
                    font-weight: 400;
                    line-height: 20px;
                    letter-spacing: 0em;
                    text-align: center;
                }
                .sort-routes-title {
                    font-family: Maersk Text;
                    font-weight: 400;
                    font-size: 14px;
                    line-height: 20px;
                    text-align: left;
                    margin-left: 10px
                }
                .route-action {
                    font-weight: 400;
                    font-size: 14px;
                    line-height: 20px;
    
                }
                .action-wrapper {
                    display: flex;
                    flex-direction: row;
                    grid-column: 1 / -1;
                    align-items: center;
                    justify-content: space-between;
                    margin-top: 16px;
                    column-gap: 8px;
                }
                .bottom-row {   
                    margin-bottom: 16px;
                }
                .sticky-footer {
                    background-color: #f8f8f8;
                    padding: 8px 0;
                    text-align: center;
                    position: -webkit-sticky;
                    position: sticky;
                    bottom: 0;
                    z-index: 500;
                    border-top: 1px solid transparent; /* Ensure the border doesn't affect the box dimensions */
                    box-shadow: 0 -10px 10px -5px rgba(0, 0, 0, 0.1);
                    margin-top: 16px;
                }
                .route-card-item.selected {
                    border: 2px solid var(--selected-border-color);
                    background-color: var(--selected-background-color);
                }
                .export-option {
                    margin-top: 0px;
                }
                .route-data {
                    display: grid;
                }
                .route-tile-delete {
                    display: flex;
                    font-size: 16px;
                    font-weight: 700;
                    line-height: 24px;
                    font-family: Maersk Text;
                    padding-top: 16px;
                    gap:10px;
                }
                .delete-icon {
                    position: absolute;
                    right: 20px;
                    cursor: pointer;
                }
                .footer-text {
                    font-size: 12px;
                    font-family: 'MaerskText';
                }
                .loader-container{
                    padding: 100px;
                }
                .loader-container .loader {
                    position: relative;
                    left: 50%;
                }
                .failed-route {
                    color: #B80012;
                }
                .exclamation-icon {
                    position: relative;
                    left: 50%;
                    height: 20px;
                    width: 20px;
                }
                .failed-container {
                    padding-top:50px;
                    padding-bottom:50px;
                }
                #Sort-routes { 
                    border: 1px solid #cfcfcf;
                    padding: 0.4rem;
                    margin-top: 1px;
                    height: 90%; 
                    width: 90%;  
                }
                .route-name {  
                    max-width: 140px;
                    overflow: hidden;
                    white-space: nowrap;
                    text-overflow: ellipsis;
                    padding-right: 10px;
                    font-weight: 600;
                }
                .success {
                    background: rgb(197, 229, 194);
                    box-shadow: rgba(0, 0, 0, 0.25) 4px 4px 8px 2px;
                    border-radius: 4px;
                    position: absolute;
                    top: 1rem;
                    left: 0.5rem;
                    display: flex;
                    padding: 10px;
                    align-items: center;
                    justify-content: center;
                    transition: transform 0.3s ease-in-out;
                    z-index: 9999;
                    gap: 10px;
                    .text{
                        font-family: 'MaerskText';
                    }
                    .icon{
                        padding-top: 6px;
                        width: 12px;
                    }
                    .close{
                        cursor: pointer;
                        padding-left: 10px;
                    }
                }
                 /* Accordion styles */
                    .accordion {
                        border: 1px solid #ddd;
                        border-radius: 4px;
                        position: relative;
                        .accordion-section-header{
                            display: flex;
                            justify-content: center;
                            background-color:#F7F7F7;
                        }
                        .accordian-icon{
                        width: 16px;
                        height: 16px;
                        position: absolute;
                        top: -15px;
                        transition: transform 0.3s ease;
                        background-color: #fff;
                        border-radius: 50%;
                        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                        padding: 5px;
                        cursor: pointer;
                        }
                        .accordian-icon.expanded {
                            transform: rotate(180deg); /* Rotate icon by 180 degrees when expanded */
                        }
                    }
                    .accordion-section {
                        padding: 10px 20px;
                        text-align: center;
                        font-family: 'MaerskText';
                        font-weight: 400;
                        font-size: 14px;
                        color:#767676;
                    }
                    .accordion-content {
                        display: block;
                        padding: 10px 20px;
                    }
                    .bpr-loader{
                        display: flex;
                        flex-direction: column;
                        justify-content: center;
                        align-items: center;
                        text-align: center;
                        border-radius: 4px;
                        gap: 30px;
                        height: 270px;
                        font-family: MaerskText;
                        border-radius: 4px;
                        padding: 0px 16px;
                        margin-bottom: 8px;
                        border: 2px solid var(--neutrals-grey-06, grey);
                        p{
                            font-size: 14px;
                            color: #595858;
                        }
                    }
                    .bpr-progress-loader {
                        justify-content: center;
                        gap: 12px;
                        height: 270px;
                        padding: 16px;
                        box-sizing: border-box;
                        overflow: hidden;
                    }
                    .bpr-estimate {
                        display: flex;
                        align-items: center;
                        gap: 6px;
                        font-size: 12px;
                        color: #353535;
                    }
                    .bpr-clock {
                        width: 12px;
                        height: 12px;
                        border: 1.5px solid #42b0d5;
                        border-radius: 50%;
                        position: relative;
                    }
                    .bpr-clock::before {
                        content: '';
                        position: absolute;
                        width: 1px;
                        height: 4px;
                        background: #42b0d5;
                        left: 5px;
                        top: 2px;
                    }
                    .bpr-clock::after {
                        content: '';
                        position: absolute;
                        width: 4px;
                        height: 1px;
                        background: #42b0d5;
                        left: 5px;
                        top: 6px;
                        transform: rotate(25deg);
                        transform-origin: left center;
                    }
                    .bpr-progress-toggle {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        gap: 8px;
                        border: 0;
                        background: transparent;
                        color: #595858;
                        font: inherit;
                        font-size: 13px;
                        cursor: pointer;
                        padding: 4px;
                    }
                    .bpr-header-loader {
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        flex: 0 0 20px;
                        width: 20px;
                        height: 20px;
                        overflow: hidden;
                    }
                    .bpr-header-loader mc-loading-indicator {
                        display: block;
                        width: 20px;
                        height: 20px;
                    }
                    .bpr-chevron {
                        width: 6px;
                        height: 6px;
                        border-right: 1px solid currentColor;
                        border-bottom: 1px solid currentColor;
                        transform: rotate(45deg) translateY(-2px);
                        transition: transform 150ms ease;
                    }
                    .bpr-chevron.expanded {
                        transform: rotate(225deg) translate(-1px, -1px);
                    }
                    .bpr-progress-timeline {
                        margin: 4px 0 0;
                        padding: 0 8px;
                        width: 100%;
                        overflow-y: auto;
                        text-align: left;
                        color: #595858;
                        font-size: 12px;
                    }
                    .bpr-progress-timeline mc-step-indicator-item {
                        min-height: 48px;
                    }
                    .bpr-step-copy small {
                        display: block;
                        margin-top: 3px;
                        color: #767676;
                        font-size: 11px;
                        font-style: italic;
                    }
                    .bpr-loader-error {
                        gap: 16px;
                    }
                    .bpr-loader-error p {
                        max-width: 220px;
                        max-height: 150px;
                        overflow-y: auto;
                        overflow-wrap: anywhere;
                        margin: 0;
                        white-space: pre-line;
                    }
                    .bpr-error-icon {
                        width: 20px;
                        height: 20px;
                    }
                    .bpr-retry-button {
                        border: 0;
                        border-radius: 4px;
                        background: #b80012;
                        color: #fff;
                        padding: 8px 14px;
                        font: inherit;
                        cursor: pointer;
                    }
                    .test-button{
                        margin-bottom: 10px;
                    }

                    .route-hide-show{
                        position: absolute;  
                        right: 54px; 
                    }
                    .wr-label-content{
                        background-color: azure;
                        font-size: 12px;
                        padding: 10px;
                        border-radius: 100px;
                    }
                    .wr-label{
                        display:flex;
                        justify-content:center;
                        margin-top:8px;
                    }
                    .wr-info-text{
                        font-size:12px;
                        margin-top: 10px;
                    }
                    .mc-menu{
                        min-width: 200px;
                    }
`;
